import {useAddNotification, useTransition} from '@logistics-frontend/blocks'
import {ServerError} from '@logistics-frontend/core/utils/network'
import {getErrorMessage} from '@logistics-frontend/utils'
import {useCallback} from 'react'

import {fetchCards} from '../api'
import {CardPayloadResponse} from '../types'
import {useConfigContext} from '../ui'

export function useFetchCardsTransition(cardScope?: string) {
  const {clientId = '', country, makeUrl} = useConfigContext()

  const [{payload, isPending, error}] = useTransition<CardPayloadResponse, ServerError>(
    () => fetchCards(clientId, cardScope, country, makeUrl),
    {
      key: buildKey(cardScope),
    },
  )

  return {
    cardsPayload: payload,
    isCardsPending: isPending,
    cardsError: error,
  }
}

type Options = {
  onSuccess?: (value: CardPayloadResponse) => void
  onError?: (error: ServerError) => void
}

export function useFetchCards(cardScope?: string) {
  const addNotification = useAddNotification()
  const {clientId = '', country, makeUrl} = useConfigContext()

  const [{payload, isPending, error}, startTransition] = useTransition<CardPayloadResponse, ServerError>(undefined, {
    key: buildKey(cardScope),
  })

  const fetchCardsHandler = useCallback(
    (options?: Options) => {
      startTransition(
        () => {
          return fetchCards(clientId, cardScope, country, makeUrl)
        },
        {
          onCommit(payload) {
            options?.onSuccess?.(payload)
          },
          onError(error) {
            addNotification(getErrorMessage(error), {type: 'error'})
            options?.onError?.(error)
          },
        },
      )
    },
    [addNotification, cardScope, clientId, country, makeUrl],
  )

  return {
    fetchCardsHandler,
    cardsPayload: payload,
    isCardsPending: isPending,
    cardsError: error,
  }
}

const buildKey = (cardScope?: string) => `fetch-cards-${cardScope}`
